import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportAgree,
  ReportCheck,
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportFlowPages,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewVesselListIdentityDoc, CrewVesselListResponse, CrewVesselListRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function fullName(row: CrewVesselListRow): string {
  return [row.lastName, row.firstName, row.middleName].filter(Boolean).join(' ').trim() || '—';
}

function birthLine(row: CrewVesselListRow): string {
  const date = row.birthDate ? formatDateGlobal(row.birthDate) : '';
  const place = row.birthPlace?.trim() || '';
  if (date && place) return `${date}, ${place}`;
  return date || place || '—';
}

function pickIdentityDoc(row: CrewVesselListRow): CrewVesselListIdentityDoc | null {
  if (row.passport?.number) return row.passport;
  if (row.seamanBook?.number) return row.seamanBook;
  return row.passport ?? row.seamanBook ?? null;
}

function ImoTableHead() {
  return (
    <table className="imo-crew-list-table imo-crew-list-table-head">
      <thead>
        <tr>
          <th className="imo-crew-list-col-no">No.</th>
          <th>6. Family name, given names</th>
          <th>7. Rank or rating</th>
          <th>8. Nationality</th>
          <th>9. Date and place of birth</th>
          <th>10. Gender</th>
          <th>11. Nature of identity document</th>
          <th>12. Number of identity document</th>
          <th>13. Issuing State</th>
          <th>14. Expiry date</th>
        </tr>
      </thead>
    </table>
  );
}

function ImoCrewRow({ row, index }: { row: CrewVesselListRow; index: number }) {
  const doc = pickIdentityDoc(row);
  return (
    <table className="imo-crew-list-table imo-crew-list-row-table">
      <tbody>
        <tr>
          <td className="imo-crew-list-col-no">{index + 1}</td>
          <td>{fullName(row)}</td>
          <td>{dash(row.lastRank)}</td>
          <td>{dash(row.nationality)}</td>
          <td>{birthLine(row)}</td>
          <td>{dash(row.gender)}</td>
          <td>{dash(doc?.nature)}</td>
          <td>{dash(doc?.number)}</td>
          <td>{dash(doc?.issuingState)}</td>
          <td>{fmtDate(doc?.expiredDate)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type ImoCrewListReportProps = {
  data: CrewVesselListResponse;
  companyLogoSrc?: string | null;
  companyName?: string | null;
  /** Port of arrival/departure (blank for master to complete if omitted). */
  portName?: string | null;
  /** Date of arrival/departure (falls back to list period if omitted). */
  arrivalDate?: string | null;
  /** Last port of call (blank if omitted). */
  lastPort?: string | null;
  voyageNumber?: string | null;
  isArrival?: boolean;
  isDeparture?: boolean;
};

export const ImoCrewListReport: React.FC<ImoCrewListReportProps> = ({
  data,
  companyLogoSrc = null,
  companyName = null,
  portName = null,
  arrivalDate = null,
  lastPort = null,
  voyageNumber = null,
  isArrival = false,
  isDeparture = false,
}) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const flag = vessel?.flag?.name || vessel?.flag?.isoCode2 || '—';
  const arrivalDateLabel = arrivalDate
    ? fmtDate(arrivalDate)
    : data.periodFrom && data.periodTo && data.periodFrom !== data.periodTo
      ? `${fmtDate(data.periodFrom)} – ${fmtDate(data.periodTo)}`
      : fmtDate(data.periodFrom || data.periodTo);
  const rows = data.rows ?? [];
  const totalCrew = rows.length;

  const firstHeader = useMemo(
    () => (
      <div className="imo-crew-list-flow-header">
        <ReportHeader>
          <ReportTitleRow
            title="IMO Crew List"
            formId="FAL Form 5"
            logoSrc={companyLogoSrc}
            logoAlt={companyName || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="1.1 Name of ship" value={vesselName} />
              <ReportField label="1.2 IMO number" value={dash(vessel?.imoNumber)} />
              <ReportField label="1.3 Call sign" value={dash(vessel?.callSign)} />
              <ReportField label="1.4 Voyage number" value={dash(voyageNumber)} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="2. Port of arrival / departure" value={dash(portName)} />
              <ReportField label="3. Date of arrival / departure" value={arrivalDateLabel} />
              <ReportField label="4. Flag State of ship" value={flag} />
              <ReportField label="5. Last port of call" value={dash(lastPort)} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Arrival / departure" colSpan={2}>
                <span className="imo-crew-list-checks">
                  <ReportCheck label="Arrival" checked={isArrival} />
                  <ReportCheck label="Departure" checked={isDeparture} />
                </span>
              </ReportField>
              <ReportField label="Persons on board" value={String(totalCrew)} colSpan={2} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <ImoTableHead />
      </div>
    ),
    [
      arrivalDateLabel,
      companyLogoSrc,
      companyName,
      flag,
      isArrival,
      isDeparture,
      lastPort,
      portName,
      totalCrew,
      vessel?.callSign,
      vessel?.imoNumber,
      vesselName,
      voyageNumber,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <div className="imo-crew-list-flow-header">
        <ReportTitleRow title="IMO Crew List" formId="FAL Form 5 — continued" />
        <ImoTableHead />
      </div>
    ),
    [],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportAgree>
          I hereby declare that the particulars given in this Crew List are correct and complete.
        </ReportAgree>
        <ReportSigns columns={2}>
          <ReportSignLine label="Date and signature by master, authorized agent or officer" />
          <ReportSignLine label="Stamp" />
        </ReportSigns>
        {companyName ? (
          <ReportNote>
            Issued by {companyName}
            {vessel?.officialNo ? ` · Official no. ${vessel.officialNo}` : ''}
          </ReportNote>
        ) : null}
      </ReportFooter>
    ),
    [companyName, vessel?.officialNo],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="imo-crew-list-table imo-crew-list-row-table">
            <tbody>
              <tr>
                <td colSpan={10} className="imo-crew-list-empty">
                  No crew assigned for this vessel and period.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => <ImoCrewRow key={row.transactionId} row={row} index={i} />),
      itemKeys: rows.map((row) => row.transactionId),
    };
  }, [rows]);

  return (
    <ReportFlowPages
      className="imo-crew-list-report"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ImoCrewListReport;
